import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Custom hook to handle page container logic
 * @param {Object} config - Configuration for the page container
 * @param {string} config.basePath - The base path for this section (e.g., 'profile', 'learning')
 * @param {string} config.defaultPage - The default page to show if no specific page is in URL
 * @param {Object} config.pages - Mapping of page IDs to their components and other properties
 * @param {Object} config.pageTitles - Mapping of page IDs to their display titles
 * @param {Function} config.breadcrumbFactory - Optional custom function to generate breadcrumbs
 * @param {Array} config.pagesWithoutIcons - Optional array of page IDs that shouldn't display icons
 * @returns {Object} Page container props and helpers
 */
export const usePageContainer = ({
  basePath,
  defaultPage,
  pages,
  pageTitles,
  breadcrumbFactory = null,
  pagesWithoutIcons = [] // New parameter - array of pages that shouldn't show icons
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Parse the current path to determine which component to show
  const pathSegments = location.pathname.split('/').filter(segment => segment);
  
  // Get the current page (or default if only base path)
  const getCurrentPage = () => {
    // If we're at the base path (e.g., /profile), use the default page
    if (pathSegments.length === 1 && pathSegments[0] === basePath) {
      return defaultPage;
    }
    
    // Otherwise, use the last path segment
    const lastSegment = pathSegments[pathSegments.length - 1];
    
    // Check if this is a valid page ID, otherwise fallback to default
    return pages[lastSegment] ? lastSegment : defaultPage;
  };
  
  const currentPage = getCurrentPage();
  
  // Get the current page component, icon, and title
  const pageComponent = pages[currentPage]?.component || pages[defaultPage].component;
  const pageIcon = pages[currentPage]?.icon || pages[defaultPage].icon;
  const pageTitle = pageTitles[currentPage] || 
    (typeof currentPage === 'string' ? 
      currentPage.charAt(0).toUpperCase() + currentPage.slice(1) : 
      pageTitles[defaultPage]);
  
  // Determine if we should show the icon for this page
  const showIcon = !pagesWithoutIcons.includes(currentPage);
  
  // Generate breadcrumbs (default implementation)
  const generateBreadcrumbs = () => {
    // Use custom factory if provided
    if (breadcrumbFactory) {
      return breadcrumbFactory(basePath, currentPage, pathSegments);
    }
    
    // Default implementation
    const breadcrumbs = [
      { label: 'Home', path: '/dashboard', active: false }
    ];
    
    // Add base path as second breadcrumb
    const basePathTitle = pageTitles[basePath] || 
      basePath.charAt(0).toUpperCase() + basePath.slice(1);
    
    breadcrumbs.push({
      label: basePathTitle,
      path: `/${basePath}`,
      active: currentPage === defaultPage
    });
    
    // Add current page if it's not the default page
    if (currentPage !== defaultPage) {
      breadcrumbs.push({
        label: pageTitle,
        path: `/${basePath}/${currentPage}`,
        active: true
      });
    }
    
    return breadcrumbs;
  };
  
  const breadcrumbs = generateBreadcrumbs();
  
  // Navigation handler for breadcrumb clicks
  const handleNavigation = (path) => {
    navigate(path);
  };
  
  return {
    currentPage,
    pageComponent,
    pageIcon,
    pageTitle,
    breadcrumbs,
    handleNavigation,
    showIcon // Include this in the return value
  };
};